import { ref, shallowRef, Ref } from 'vue'

export interface SSEEvent {
  data: string
  type: string
}

export type SSEHandler = (event: SSEEvent) => void

// Singleton state
// Shallow, so the stored request is the request itself: a dropped stream is told apart from
// one we replaced by identity, which a reactive proxy around it would never match.
const eventSource: Ref<XMLHttpRequest | null> = shallowRef(null)
const handlers: Ref<Map<string, SSEHandler[]>> = ref(new Map())
const isConnected: Ref<boolean> = ref(false)

// A stream that ends -- the server closing it, a proxy dropping an idle connection, the
// network -- has to be opened again, or every event after it is lost: an upload finishing
// or a re-classify ending only shows after a manual refresh. Back off while it keeps failing.
const RETRY_MIN_MS = 2 * 1000
const RETRY_MAX_MS = 30 * 1000
let retryTimer: ReturnType<typeof setTimeout> | null = null
let retryDelay = RETRY_MIN_MS

export interface EventSourceComposable {
  isConnected: Ref<boolean>
  connect: () => void
  disconnect: () => void
  reconnect: () => void
  subscribe: (topic: string, handler: SSEHandler) => void
  unsubscribe: (topic: string, handler: SSEHandler) => void
}

export function useEventSource(): EventSourceComposable {
  const connect = (): void => {
    const storedUser = localStorage.getItem('authenticatedUser')
    const user = storedUser ? JSON.parse(storedUser) : {}
    const token = localStorage.getItem('id_token')
    
    if (!user.username || !token) {
      console.warn('Cannot connect SSE: missing user or token')
      return
    }
    
    closeStream()
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const url = `${baseUrl}/events/${encodeURIComponent(user.username)}`
    
    // Create custom event source with auth
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    
    let lastIndex = 0
    let eventType = 'message'
    let eventData = ''
    // A delivery can end partway through a line; it waits here for the rest of it.
    let partialLine = ''

    xhr.onreadystatechange = (): void => {
      if (xhr.readyState === XMLHttpRequest.OPENED) {
        isConnected.value = true
      }
      if (xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED && xhr.status === 200) {
        retryDelay = RETRY_MIN_MS
      }
    }

    xhr.onprogress = (): void => {
      const text = partialLine + xhr.responseText.substring(lastIndex)
      lastIndex = xhr.responseText.length

      // Parse SSE format. Lines may end in \r\n as well as \n.
      const lines = text.split(/\r?\n/)
      partialLine = lines.pop() ?? ''

      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventType = line.substring(6).trim()
        } else if (line.startsWith('data:')) {
          eventData = line.substring(5).trim()
        } else if (line === '' && eventData) {
          // Dispatch event
          const eventHandlers = handlers.value.get(eventType) || []
          eventHandlers.forEach(handler => {
            try {
              handler({ data: eventData, type: eventType })
            } catch (e) {
              console.error('SSE handler error:', e)
            }
          })
          eventData = ''
          eventType = 'message'
        }
      }
    }
    
    xhr.onerror = (error): void => {
      console.error('SSE error:', error)
      isConnected.value = false
    }
    
    // Fires however the request ends. One we replaced or closed ourselves is not ours to reopen.
    xhr.onloadend = (): void => {
      if (eventSource.value !== xhr) return
      eventSource.value = null
      isConnected.value = false
      retryTimer = setTimeout(() => {
        retryTimer = null
        connect()
      }, retryDelay)
      retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS)
    }

    xhr.send()
    eventSource.value = xhr
  }
  
  // Closes the current stream but keeps the backoff, which a retry carries forward.
  const closeStream = (): void => {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
    if (eventSource.value) {
      // Let go first: aborting fires loadend, which must not read as a dropped stream.
      const xhr = eventSource.value
      eventSource.value = null
      xhr.abort()
    }
    isConnected.value = false
  }

  const disconnect = (): void => {
    closeStream()
    retryDelay = RETRY_MIN_MS
  }
  
  const reconnect = (): void => {
    disconnect()
    setTimeout(connect, 100)
  }
  
  const subscribe = (topic: string, handler: SSEHandler): void => {
    if (!handlers.value.has(topic)) {
      handlers.value.set(topic, [])
    }
    handlers.value.get(topic)!.push(handler)
  }
  
  const unsubscribe = (topic: string, handler: SSEHandler): void => {
    const topicHandlers = handlers.value.get(topic)
    if (topicHandlers) {
      const index = topicHandlers.indexOf(handler)
      if (index > -1) {
        topicHandlers.splice(index, 1)
      }
    }
  }
  
  return {
    isConnected,
    connect,
    disconnect,
    reconnect,
    subscribe,
    unsubscribe
  }
}
